import React, { useState, useEffect } from 'react';
import { useLazyQuery, gql } from "@apollo/client";
import useApi from "../../api";
import GraphQLStateBasedPagination from "../common/graphQLStatebasedPagination";
import NxtbnModal from "../Modal";

const GET_IMAGE_LIST = gql`
  query GetImageList($first: Int, $after: String, $last: Int, $before: String) {
    images(first: $first, after: $after, last: $last, before: $before) {
      edges {
        node {
          id
          dbId
          imageXs
          name
          createdAt
        }
        cursor
      }
      pageInfo {
        endCursor
        hasNextPage
        startCursor
        hasPreviousPage
      }
    }
  }
`;

const GET_PREVIEW_IMAGE = gql`
    query GetPreviewImage($id: ID!) {
        image(id: $id) {
            image
        }
    }
`;

interface ImageChooseModalProps {
    onClose: () => void;
    onSelectedSave: (data: any) => void;
    isOpen: boolean;
    isMull?: boolean;
}

interface ImageNode {
    id: string;
    dbId: number;
    imageXs: string;
    name: string;
    createdAt: string;
}

interface SelectedImage {
    id: number;
    image: string;
}

interface PageInfo {
    endCursor: string;
    hasNextPage: boolean;
    startCursor: string;
    hasPreviousPage: boolean;
}

const ImageChooseModal: React.FC<ImageChooseModalProps> = ({ onClose, onSelectedSave, isOpen, isMull }) => {
    const [selectImage, setSelectImage] = useState<SelectedImage[]>([]);
    // const [previewImage, setPreviewImage] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    const [getPreviewImage, { data: previewData, error: previewError }] = useLazyQuery(GET_PREVIEW_IMAGE);

    
    // Pagination state tracking
    const [paginationState, setPaginationState] = useState<{
        first: number | null;
        after: string | null;
        last: number | null;
        before: string | null;
    }>({
        first: 24,
        after: null,
        last: null,
        before: null
    });
    
    const [getImages, { loading, error, data, refetch }] = useLazyQuery(GET_IMAGE_LIST, {
        variables: {
            first: paginationState.first,
            after: paginationState.after,
            last: paginationState.last,
            before: paginationState.before
        },
    });
    
    const api = useApi();

    useEffect(() => {
        if (isOpen) {
            getImages();
        }
    }, [isOpen, getImages, paginationState]);

    const onSelectImage = (img: ImageNode) => {
        const isSelected = selectImage.some(selected => selected.id === img.dbId);
        
        if (isSelected) {
            // If already selected, remove it
            setSelectImage(selectImage.filter(selected => selected.id !== img.dbId));
        } else {
            // Fetch the high-res preview image before updating state
            getPreviewImage({ 
                variables: { id: img.dbId },
                onCompleted: (data) => {
                    const highResImage = data?.image?.image || img.imageXs;
                    
                    if (!isMull && selectImage.length >= 1) {
                        // Single selection mode - replace the current selection
                        setSelectImage([{ id: img.dbId, image: highResImage }]);
                    } else {
                        // Multi selection mode - add to current selection
                        setSelectImage([...selectImage, { id: img.dbId, image: highResImage }]);
                    }
                }
            });
        }
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const formData = {
            name: file.name,
            image: file,
            image_alt_text: file.name
        };

        try {
            await api.postImage(formData);
            refetch();
        } catch (error) {
            console.error('Upload failed:', error);
        }
    };

    const deleteSelectedImages = async (e:any) => {
        e.preventDefault();
        try {
            for (const img of selectImage) {
                await api.deleteImage(img.id);
            }
            setSelectImage([]);
            refetch();
        } catch (error) {
            console.error('Delete failed:', error);
        }
    };

    const onSave = () => {
        if (selectImage.length === 0) {
            onClose();
            return;
        }

        if (!isMull && selectImage.length > 1) {
            return;
        }

        onSelectedSave(selectImage);
        onClose();
        setSelectImage([]);
    };

    // Reset pagination - we'll call this from our component
    const resetPagination = () => {
        const pageSize = paginationState.first || paginationState.last || 18;
        setPaginationState({
            first: pageSize,
            after: null,
            last: null,
            before: null
        });
    };

    // Get pageInfo from data or default to empty object
    const pageInfo: PageInfo = data?.images?.pageInfo || {
        hasNextPage: false,
        hasPreviousPage: false,
        endCursor: '',
        startCursor: ''
    };

    if (!isOpen) return null;

    const images = data?.images?.edges?.map((edge: any) => edge.node) || [];
    const filteredImages = images.filter((img: ImageNode) => 
        img.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <NxtbnModal isOpen={isOpen} onClose={onClose} size="xxl">
            <div className="bg-white rounded-xl w-full h-[90vh] flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b flex justify-between items-center bg-white">
                    <div className="flex items-center space-x-4">
                        <h2 className="text-2xl font-semibold text-gray-900">Image Library</h2>
                        {selectImage.length > 0 && (
                            <span className="px-2.5 py-1 bg-[#0CAF60]/10 text-[#0CAF60] rounded-full text-sm font-medium">
                                {selectImage.length} selected
                            </span>
                        )}
                    </div>
                    <button 
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100 transition-all"
                    >
                        <span className="text-xl">×</span>
                    </button>
                </div>

                {/* Main Content */}
                <div className="flex flex-1 overflow-hidden">
                    {/* Left Sidebar - Image Grid */}
                    <div className="w-3/4 p-6 overflow-y-auto">
                        <div className="flex justify-between items-center mb-6 gap-4">
                            <div className="relative flex-1 max-w-md">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                    🔍
                                </span>
                                <input
                                    type="text"
                                    placeholder="Search media..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 pr-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0CAF60] w-full bg-gray-50 hover:bg-white transition-colors"
                                />
                            </div>
                            <label className="flex items-center gap-2 px-4 py-2.5 bg-[#0CAF60] text-white rounded-lg cursor-pointer hover:bg-[#0CAF60]/90 transition-colors">
                                <span className="text-lg">↑</span>
                                Upload Media
                                <input
                                    type="file"
                                    className="hidden"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                />
                            </label>
                        </div>

                        {loading ? (
                            <div className="flex flex-col items-center justify-center h-64 space-y-4">
                                <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#0CAF60] border-t-transparent"></div>
                                <p className="text-gray-500">Loading media...</p>
                            </div>
                        ) : filteredImages.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-64 space-y-4 text-gray-400">
                                <span className="text-4xl">🖼️</span>
                                <p>No images found</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                                {filteredImages.map((img: ImageNode) => {
                                    const isSelected = selectImage.some(selected => selected.id === img.dbId);
                                    return (
                                        <div
                                            key={img.id}
                                            onClick={() => onSelectImage(img)}
                                            className={`relative aspect-square rounded-xl overflow-hidden cursor-pointer group transition-all duration-200 hover:scale-[1.02] ${
                                                isSelected ? 'ring-4 ring-[#0CAF60]' : 'hover:ring-2 hover:ring-[#0CAF60]/50'
                                            }`}
                                        >
                                            <img
                                                src={img.imageXs}
                                                alt={img.name}
                                                className="w-full h-full object-cover"
                                            />
                                            <div className={`absolute inset-0 bg-gradient-to-t from-black/60 to-transparent transition-opacity ${
                                                isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                                            }`}>
                                                <div className="absolute top-3 right-3">
                                                    <div className={`w-7 h-7 rounded-full ${
                                                        isSelected ? 'bg-[#0CAF60]' : 'bg-white'
                                                    } flex items-center justify-center shadow-lg`}>
                                                        {isSelected ? (
                                                            <span className="text-white text-sm">✓</span>
                                                        ) : (
                                                            <span className="text-gray-600 text-sm">+</span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="absolute bottom-3 left-3 right-3">
                                                    <p className="text-white text-sm truncate">{img.name}</p>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Right Sidebar - Preview */}
                    <div className="w-1/4 border-l bg-gray-50 p-6 overflow-y-auto">
                        {previewData?.image ? (
                            <div className="space-y-4">
                                <div className="aspect-square rounded-xl overflow-hidden bg-white shadow-md">
                                    <img
                                        src={previewData?.image?.image}
                                        alt="Preview"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="bg-white rounded-lg p-4 shadow-sm">
                                    <h3 className="font-medium text-gray-900 mb-2">Selected Media</h3>
                                    <p className="text-sm text-gray-500">
                                        {selectImage.length} image{selectImage.length !== 1 ? 's' : ''} selected
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full space-y-4 text-gray-400">
                                <span className="text-4xl">🖼️</span>
                                <p>Select an image to preview</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Using our reusable pagination component */}
                <GraphQLStateBasedPagination 
                    paginationState={paginationState}
                    setPaginationState={setPaginationState}
                    pageInfo={pageInfo}
                    loading={loading}
                />

                {/* Footer */}
                <div className="px-6 py-4 border-t bg-gray-100 flex justify-between items-center">
                    <div className="flex space-x-2">
                        {selectImage.length > 0 && (
                            <button
                                onClick={deleteSelectedImages}
                                className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                            >
                                <span className="text-lg">🗑️</span>
                                Delete Selected
                            </button>
                        )}
                    </div>
                    <div className="flex space-x-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={onSave}
                            className="px-4 py-2 bg-[#0CAF60] text-white rounded-lg hover:bg-[#0CAF60]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={!isMull && selectImage.length > 1}
                        >
                            Save Selection
                        </button>
                    </div>
                </div>
            </div>
        </NxtbnModal>
    );
};

export default ImageChooseModal;